import { displayNameOf } from '@/shared/components/accountIdentity';

/**
 * How a person's agent commits read, and what the service says when it refuses
 * an address (spec 158). Pure: no React, no client.
 */

/**
 * The `Name <email>` line the agent's commits in this person's runs carry: their
 * commit email, else their linked account's forge commit email (spec 159), else
 * their sign-in email — the order the service authors with (spec 139 FR-021).
 */
export function authorLine(
  user: { display_name: string; email: string },
  commitEmail: string | null,
  linkedEmail: string | null = null,
): string {
  const email = commitEmail ?? linkedEmail ?? user.email;
  return `${displayNameOf(user.display_name, user.email)} <${email}>`;
}

/** Where the author line's email comes from, for the line beneath it. */
export function authorSource(
  commitEmail: string | null,
  linkedEmail: string | null,
): 'commit' | 'linked' | 'sign-in' {
  if (commitEmail !== null) return 'commit';
  if (linkedEmail !== null) return 'linked';
  return 'sign-in';
}

/**
 * Why the service refused an address, from its 422 body.
 *
 * Read defensively: the generated type for this status is FastAPI's
 * `HTTPValidationError`, but the service answers with its own violations
 * envelope, `{ violations: [{ path, message }] }` (research R2) — the same gap
 * `useTaskLifecycle` documents for reruns. The validator's prefix is dropped
 * because the field already says the address is not one.
 */
export function commitEmailRefusal(body: unknown): string | null {
  if (typeof body !== 'object' || body === null || !('violations' in body)) {
    return null;
  }
  const { violations } = body;
  if (!Array.isArray(violations)) return null;
  for (const violation of violations as unknown[]) {
    if (typeof violation !== 'object' || violation === null) continue;
    const { path, message } = violation as {
      path?: unknown;
      message?: unknown;
    };
    if (path === 'commit_email' && typeof message === 'string') {
      return message.replace(/^value is not a valid email address:\s*/i, '');
    }
  }
  return null;
}

/**
 * What saving `text` would send: the trimmed address, `null` to clear a saved
 * one, or `undefined` when nothing would change and nothing should be sent.
 */
export function commitEmailToSend(
  text: string,
  saved: string | null,
): string | null | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return saved === null ? undefined : null;
  return trimmed === saved ? undefined : trimmed;
}
